import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Handshake, TrendingUp, Megaphone, PhoneCall, DollarSign, ArrowRight, Check,
  Sparkles, ShieldCheck, Users, HeadphonesIcon, Loader2, CheckCircle2, ArrowLeft,
} from "lucide-react";
import { Button, Input, Textarea, toast } from "@/components/ds";
import { SmsConsent } from "@/components/SmsConsent";
import { api, formatApiError } from "@/lib/api";

const CALENDLY_URL = "https://calendly.com/claytondrewholdings/15min";

function openCalendly(e?: React.MouseEvent) {
  e?.preventDefault();
  const w = window as unknown as { Calendly?: { initPopupWidget: (o: { url: string }) => void } };
  if (w.Calendly) w.Calendly.initPopupWidget({ url: CALENDLY_URL });
  else window.open(CALENDLY_URL, "_blank", "noopener");
}

const STEPS = [
  { icon: Megaphone, n: "1", title: "You Generate the Lead", body: "Your agency generates calls and inquiries through the marketing services you already provide your home-service clients." },
  { icon: PhoneCall, n: "2", title: "ResFil Captures the Opportunity", body: "ResFil's AI receptionist answers inquiries, captures leads, communicates with customers and helps schedule appointments — 24/7." },
  { icon: DollarSign, n: "3", title: "Your Agency Earns", body: "Eligible referred customers can generate recurring commission revenue for your agency under the applicable Agency Partner Agreement." },
];

const WHY = [
  { icon: TrendingUp, t: "Recurring commission opportunity" },
  { icon: Users, t: "Adds real value for your existing contractor clients" },
  { icon: PhoneCall, t: "Helps clients capture more of the leads you already generate" },
  { icon: Sparkles, t: "ResFil handles product demos" },
  { icon: Handshake, t: "ResFil handles the sales process" },
  { icon: ShieldCheck, t: "ResFil handles onboarding" },
  { icon: DollarSign, t: "ResFil handles billing" },
  { icon: HeadphonesIcon, t: "ResFil handles product support" },
  { icon: Check, t: "Nationwide eligibility across the United States" },
];

const INDUSTRIES = ["HVAC", "Plumbing", "Roofing", "Electrical"];

const LEVELS = [
  { name: "Standard Agency Partner", body: "Begins with a 20% recurring commission structure on eligible monthly subscription revenue.", popular: false },
  { name: "Preferred Agency Partner", body: "For agencies with a growing book of referred clients — may include different or enhanced commercial terms.", popular: true },
  { name: "Strategic Agency Partner", body: "For high-volume and deeply integrated partners — individually negotiated commercial terms.", popular: false },
];

export default function Partners() {
  const formRef = useRef<HTMLDivElement>(null);
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div data-testid="partners-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="pointer-events-none absolute right-0 top-0 h-full w-1/2 bg-gradient-to-l from-blue-50/70 to-transparent" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900" data-testid="partners-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
            <Handshake className="h-4 w-4" /> ResFil Agency Partner Program
          </div>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Turn Better Lead Response Into <span className="text-blue-600">Recurring Revenue</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-slate-600 sm:text-lg">
            Partner with ResFil to help your home-service clients capture more of the opportunities your agency already generates — while creating a recurring revenue stream for your agency.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button size="lg" onClick={scrollToForm} data-testid="hero-get-info-btn">Get More Info <ArrowRight className="ml-1 h-4 w-4" /></Button>
            <Button size="lg" variant="outline" onClick={openCalendly} data-testid="hero-partnership-call-btn">Book a Partnership Call</Button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">How the partnership works</h2>
        <p className="mt-2 max-w-2xl text-slate-600">Your agency generates the opportunity. ResFil helps answer and convert it. Your contractor client performs the job.</p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`how-step-${s.n}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white"><s.icon className="h-5 w-5" /></div>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-blue-600">Step {s.n}</div>
              <h3 className="mt-1 font-display text-lg font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Why agencies partner with ResFil</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w) => (
              <div key={w.t} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="why-item">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><w.icon className="h-4.5 w-4.5" /></div>
                <p className="text-sm font-medium text-slate-700">{w.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 text-center">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Built for home-service industries</h2>
        <p className="mx-auto mt-2 max-w-2xl text-slate-600">ResFil currently works with qualifying businesses in these industries anywhere in the United States.</p>
        <div className="mx-auto mt-8 flex flex-wrap justify-center gap-3" data-testid="industries-row">
          {INDUSTRIES.map((i) => (
            <span key={i} className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm">{i}</span>
          ))}
        </div>
      </section>

      {/* PARTNER LEVELS */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Partner levels</h2>
          <p className="mt-2 max-w-2xl text-slate-600">As your referred client base grows, so can the relationship.</p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {LEVELS.map((l) => (
              <div key={l.name} className={`relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm ${l.popular ? "border-blue-500 ring-2 ring-blue-500" : "border-slate-200"}`} data-testid={`level-${l.name.split(" ")[0].toLowerCase()}`}>
                {l.popular && <span className="absolute -top-3 left-6 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white shadow">GROWING PARTNERS</span>}
                <h3 className="font-display text-lg font-semibold text-slate-900">{l.name}</h3>
                <p className="mt-2 text-sm text-slate-600">{l.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-3xl text-xs text-slate-500">
            Setup fees are not commissionable. All commissions and eligibility are subject to the applicable signed ResFil Agency Partner Agreement. Program terms may change and individually negotiated terms may apply to Preferred and Strategic relationships.
          </p>
        </div>
      </section>

      {/* GET MORE INFO FORM */}
      <section ref={formRef} className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20" data-testid="partner-form-section">
        <div className="text-center">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Get more info</h2>
          <p className="mx-auto mt-2 max-w-xl text-slate-600">Tell us about your agency and we'll follow up with everything you need to get started.</p>
        </div>
        <PartnerForm />
      </section>
    </div>
  );
}

function PartnerForm() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    first_name: "", last_name: "", agency_name: "", email: "", phone: "",
    website: "", client_count: "", industries: "", message: "",
  });
  // A2P 10DLC: consent captured at the point of opt-in, never pre-checked.
  const [smsConsent, setSmsConsent] = useState(false);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const k of ["first_name", "last_name", "agency_name", "email", "phone"] as const) {
      if (!f[k].trim()) { toast.error("Please complete all required fields."); return; }
    }
    if (!smsConsent) { toast.error("Please agree to receive SMS messages to continue."); return; }
    setBusy(true);
    try {
      await api.partnerInquiry({
        ...f,
        website: f.website || undefined, client_count: f.client_count || undefined,
        industries: f.industries || undefined, message: f.message || undefined,
        sms_consent: smsConsent,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-10 text-center" data-testid="partner-success">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><CheckCircle2 className="h-7 w-7" /></div>
        <h3 className="mt-5 font-display text-xl font-semibold text-slate-900">Thanks — your inquiry is in.</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">A member of our partnerships team will reach out shortly. We've also sent a confirmation to your email.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="partner-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name*"><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="pf-first-name" /></Field>
        <Field label="Last name*"><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="pf-last-name" /></Field>
        <Field label="Agency name*" full><Input value={f.agency_name} onChange={(e) => set("agency_name", e.target.value)} data-testid="pf-agency-name" /></Field>
        <Field label="Business email*"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="pf-email" /></Field>
        <Field label="Phone number*"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="pf-phone" /></Field>
        <Field label="Website"><Input value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" data-testid="pf-website" /></Field>
        <Field label="Approx. number of home-service clients"><Input value={f.client_count} onChange={(e) => set("client_count", e.target.value)} placeholder="e.g. 15" data-testid="pf-client-count" /></Field>
        <Field label="Primary industries served" full><Input value={f.industries} onChange={(e) => set("industries", e.target.value)} placeholder="e.g. HVAC, Plumbing" data-testid="pf-industries" /></Field>
        <Field label="Message (optional)" full><Textarea rows={3} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="pf-message" /></Field>
      </div>
      <SmsConsent checked={smsConsent} onChange={setSmsConsent} />

      <Button type="submit" className="mt-6 w-full" disabled={busy} data-testid="pf-submit">
        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : <>Get More Info</>}
      </Button>
    </form>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return <div className={full ? "sm:col-span-2" : ""}><label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>{children}</div>;
}
